import React from "react";
import { Link } from "react-router-dom";
import AI_PDF_CONST from "../../util/PdfAiConst";
import StatusBadge from "./StatusBadge";
import LoadingState from "./LoadingState";
import EmptyState from "./EmptyState";
import ErrorState from "./ErrorState";
import PageHeader from "./PageHeader";

const STATUS_OPTIONS = [
  "all",
  "draft",
  "onboarding",
  "active",
  "inactive",
  "suspended",
  "deleted",
];

const TenantList = ({
  tenants,
  loading,
  error,
  search,
  statusFilter,
  onSearchChange,
  onStatusChange,
  onRetry,
  onCreate,
  singleTenantMode,
}) => {
  const query = search.trim().toLowerCase();
  const filteredTenants = (tenants || []).filter((tenant) => {
    const matchesSearch =
      !query ||
      [tenant.name, tenant.slug, tenant.owner_name, tenant.industry, tenant.business_type]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    const matchesStatus = statusFilter === "all" || tenant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const hasQuery = Boolean(search?.trim() || statusFilter !== "all");

  if (singleTenantMode) {
    const tenant = filteredTenants[0];
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Your tenant"
          title={tenant?.name || "My tenant"}
          description={tenant ? `${tenant.slug} · ${tenant.industry || "-"} · ${tenant.business_type || "-"}` : "Loading..."}
        />

        {loading ? <LoadingState label="Loading tenant..." /> : null}
        {!loading && error ? <ErrorState description={error} onRetry={onRetry} /> : null}

        {!loading && !error && tenant ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl font-semibold text-slate-950">{tenant.name}</h2>
                  <StatusBadge status={tenant.status} />
                </div>
                <p className="mt-2 text-sm text-slate-500">{tenant.slug}</p>
              </div>
            </div>

            <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
              <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                <dt className="text-slate-500">Industry</dt>
                <dd className="font-medium text-slate-900">{tenant.industry || "-"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                <dt className="text-slate-500">Business type</dt>
                <dd className="font-medium text-slate-900">{tenant.business_type || "-"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                <dt className="text-slate-500">Owner</dt>
                <dd className="font-medium text-slate-900">{tenant.owner_name || "-"}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                <dt className="text-slate-500">Plan</dt>
                <dd className="font-medium text-slate-900">{tenant.plan || "starter"}</dd>
              </div>
            </dl>

            <div className="mt-6 border-t border-slate-100 pt-4">
              <Link
                to={AI_PDF_CONST.ROUTE.ADMIN_TENANT_DETAIL.replace(":tenant_id", tenant.id)}
                className="inline-flex items-center justify-center rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
              >
                View details
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Platform admin"
        title="Tenant management"
        description="Create and manage tenants for your Doclift."
        actionLabel={onCreate ? "Create tenant" : undefined}
        actionTo={onCreate ? AI_PDF_CONST.ROUTE.ADMIN_TENANTS_CREATE : undefined}
      />

      <div className="grid gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:grid-cols-[1fr_220px]">
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="m21 21-4.35-4.35m1.85-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search tenant name, slug, owner, or industry"
            className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
        </label>

        <label className="flex flex-col justify-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            className="bg-transparent text-sm font-medium text-slate-900 outline-none"
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status === "all" ? "All statuses" : status}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? <LoadingState label="Loading tenants..." /> : null}
      {!loading && error ? <ErrorState description={error} onRetry={onRetry} /> : null}

      {!loading && !error && filteredTenants.length === 0 ? (
        <EmptyState
          title={hasQuery ? "No tenants match your filters" : "No tenants yet"}
          description={hasQuery ? "Try adjusting the search text or status filter." : "Create the first tenant to start the platform admin flow."}
          actionLabel={hasQuery ? "Clear filters" : "Create tenant"}
          onAction={onRetry}
        />
      ) : null}

      {!loading && !error && filteredTenants.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredTenants.map((tenant) => (
            <article key={tenant.id} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{tenant.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{tenant.slug}</p>
                </div>
                <StatusBadge status={tenant.status} />
              </div>

              <dl className="mt-5 grid gap-3 text-sm">
                <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                  <dt className="text-slate-500">Industry</dt>
                  <dd className="font-medium text-slate-900">{tenant.industry || "-"}</dd>
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                  <dt className="text-slate-500">Business type</dt>
                  <dd className="font-medium text-slate-900">{tenant.business_type || "-"}</dd>
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                  <dt className="text-slate-500">Owner</dt>
                  <dd className="font-medium text-slate-900">{tenant.owner_name || "-"}</dd>
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3">
                  <dt className="text-slate-500">Plan</dt>
                  <dd className="font-medium text-slate-900">{tenant.plan || "starter"}</dd>
                </div>
              </dl>

              <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                <Link
                  to={AI_PDF_CONST.ROUTE.ADMIN_TENANT_DETAIL.replace(":tenant_id", tenant.id)}
                  className="inline-flex items-center justify-center rounded-xl bg-[#096dea] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
                >
                  View details
                </Link>
                <Link
                  to={AI_PDF_CONST.ROUTE.ADMIN_TENANT_ONBOARDING.replace(":tenant_id", tenant.id)}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Onboarding
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default TenantList;
